import { useCallback, useEffect, useId, useRef, useState, type ReactElement, type SyntheticEvent } from "react";
import { createPortal } from "react-dom";
import type { SandalyeEndReason, SandalyeJoinBlock, SandalyeLine, SandalyeStatusDto } from "@sikilesi/protocol";
import { ApiError } from "../lib/api";
import { up } from "../lib/tr";
import { Dialog } from "./Dialog";
import { Link } from "./Link";
import { useServices } from "./Services";
import { TurnstileBox } from "./TurnstileBox";

const POLL_MS = 10_000;

const CLOSED_TEXT: Record<NonNullable<SandalyeStatusDto["closed_reason"]>, string> = {
  kapali: "Balıkçı bugün iskelede yok. Sandalye boş ama kapalı.",
  balik_tutmuyorum: "Bugün balık tutmuyorum. Yarın gel, olta yine burada.",
  gunluk_dolu: "Bugünlük sıra doldu. Yarın erkenden gel.",
};

const BLOCK_TEXT: Record<SandalyeJoinBlock, string> = {
  giris: "Önce Google ile giriş yap.",
  bugun_oturdun: "Bugün zaten oturdun. Bir dert, bir gün. Yarın yine gel.",
  cihaz_hakki: "Bu bağlantıdan bugünlük oturma hakkı doldu.",
  askida: "Bu hesap bir süre sandalyeye oturamaz.",
};

const END_TEXT: Record<SandalyeEndReason, string> = {
  sure: "Süre doldu; balıkçı oltasına döndü.",
  tur: "Bu kadar dert yeter; balıkçı oltasına döndü.",
  ziyaretci: "Sandalyeden kalktın.",
  filtre: "Kurallara uymayan mesajlar yüzünden oturum bitti.",
  kuyruk_suresi: "Sırada çok bekledin; yerin boşaldı.",
  terk: "Sandalye boş kaldı; oturum kapandı.",
  kapali: "Sandalye kapandı.",
  butce: "Bugün balık tutmuyorum. Yarın gel.",
};

const mmss = (ms: number) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

function chairSign(s: SandalyeStatusDto | null): string {
  if (!s) return "sandalye";
  if (!s.open) return "kapalı";
  if (s.me?.state === "active") return "senin yerin";
  if (s.occupied) return s.queue > 0 ? `dolu · sırada ${s.queue}` : "dolu";
  return "boş";
}

/** The chair next to the fisherman: a button on the pier that opens the conversation. */
export function Sandalye(): ReactElement {
  const { api } = useServices();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<SandalyeStatusDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setStatus(await api.sandalye());
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Sandalyeye ulaşılamadı.");
    }
  }, [api]);

  // Once on mount (the sign on the chair), and back from Google sign-in (?sandalye=…#gol): open the dialog.
  useEffect(() => {
    void refresh();
    const q = new URLSearchParams(window.location.search);
    if (q.has("sandalye") || window.location.hash === "#gol-sandalye") {
      if (q.get("sandalye") === "hata") setError("Google ile giriş tamamlanamadı. Bir daha dene.");
      setOpen(true);
      q.delete("sandalye");
      const rest = q.toString();
      window.history.replaceState(null, "", `${window.location.pathname}${rest ? `?${rest}` : ""}${window.location.hash}`);
    }
  }, [refresh]);

  // Poll only while the dialog is open or the visitor holds a place.
  const holding = status?.me && status.me.state !== "ended";
  useEffect(() => {
    if (!open && !holding) return;
    const t = window.setInterval(() => void refresh(), POLL_MS);
    return () => {
      window.clearInterval(t);
    };
  }, [open, holding, refresh]);

  const sign = chairSign(status);
  return (
    <>
      <button
        type="button"
        className={`z-chair${status?.occupied ? " is-occupied" : ""}`}
        aria-haspopup="dialog"
        aria-label={`Balıkçının sandalyesi: ${sign}. Derdini anlatmak için otur.`}
        onClick={() => {
          setOpen(true);
          void refresh();
        }}
      >
        <img className="z-chair__img" src="/gol/gol-sandalye.webp" width={260} height={373} alt="" loading="lazy" decoding="async" />
        <span className="z-chair__sign">{up(sign)}</span>
      </button>
      {/* Portalled to <body>: the lake scene clips and contains its layers, the dialog must sit above everything. */}
      {createPortal(
        <Dialog
          open={open}
          title="balıkçının sandalyesi"
          onClose={() => {
            setOpen(false);
          }}
        >
          <ChairBody status={status} error={error} onStatus={setStatus} refresh={refresh} />
        </Dialog>,
        document.body,
      )}
    </>
  );
}

function ChairBody({
  status,
  error,
  onStatus,
  refresh,
}: {
  status: SandalyeStatusDto | null;
  error: string | null;
  onStatus: (s: SandalyeStatusDto) => void;
  refresh: () => Promise<void>;
}): ReactElement {
  const { api } = useServices();
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);

  if (!status) return <p className="z-mono">{error ?? "Sandalyeye bakılıyor…"}</p>;
  const me = status.me;

  if (me?.state === "active") return <Chat status={status} onStatus={onStatus} refresh={refresh} />;

  const intro = (
    <p className="z-small z-chair__intro">
      Balıkçının yanında tek bir sandalye var. Otur, tek bir derdini anlat; en fazla {Math.round(status.limits.session_seconds / 60)} dakika,{" "}
      {status.limits.max_turns} mesaj. Saçma dertte acımaz, ağır dertte yanında olur.
    </p>
  );
  const queueLine =
    status.occupied || status.queue > 0 ? (
      <p className="z-chair__queue" role="status">
        {status.occupied ? "Sandalye dolu" : "Sandalye boş"}
        {status.queue > 0 ? `, sırada ${status.queue} kişi.` : "."}
      </p>
    ) : null;

  if (me?.state === "waiting") {
    return (
      <div className="z-chair__body">
        <p className="z-chair__queue" role="status">
          Sıradasın: {me.position ?? "?"}. kişi. Sıran gelince sandalye senin; bu pencereyi açık tut.
        </p>
        <p className="z-small">Sırada en fazla {Math.round(status.limits.queue_max_wait_seconds / 60)} dakika beklenir.</p>
        <button
          type="button"
          className="z-btn z-btn--ghost"
          onClick={() => {
            void api.sandalyeLeave().then(onStatus, () => undefined);
          }}
        >
          {up("sıradan çık")}
        </button>
      </div>
    );
  }

  return (
    <div className="z-chair__body">
      {intro}
      {error ? (
        <p role="alert" className="z-chair__note">
          {error}
        </p>
      ) : null}
      {me?.state === "ended" && me.ended_reason ? <p className="z-chair__note">{END_TEXT[me.ended_reason]}</p> : null}
      {!status.open && status.closed_reason ? (
        <p className="z-chair__note" role="status">
          {CLOSED_TEXT[status.closed_reason]}
        </p>
      ) : !status.signed_in ? (
        <>
          {queueLine}
          <a className="z-btn" href={api.sandalyeLoginUrl("/")}>
            {up("google ile otur")}
          </a>
          <p className="z-small">
            Yalnız hesabının geri çevrilemez bir özeti tutulur; e-posta adresin saklanmaz. Konuşma oturum bitince silinir. Oturarak 18 yaşından
            büyük olduğunu ve <Link href="/kosullar">Kullanım Koşulları</Link>'nı kabul edersin; ayrıntılar{" "}
            <Link href="/gizlilik">Gizlilik Politikası</Link>'nda.
          </p>
        </>
      ) : status.join_block ? (
        <p className="z-chair__note" role="status">
          {BLOCK_TEXT[status.join_block]}
        </p>
      ) : (
        <>
          {queueLine}
          {joining ? <p className="z-mono">Sıraya yazılıyorsun…</p> : null}
          <TurnstileBox
            onToken={(token) => {
              setJoining(true);
              setJoinError(null);
              api.sandalyeJoin(token).then(
                (s) => {
                  setJoining(false);
                  onStatus(s);
                },
                (err: unknown) => {
                  setJoining(false);
                  setJoinError(err instanceof ApiError ? err.message : "Sıraya girilemedi.");
                },
              );
            }}
          />
          {joinError ? (
            <p role="alert" className="z-chair__note">
              {joinError}
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}

function Chat({ status, onStatus, refresh }: { status: SandalyeStatusDto; onStatus: (s: SandalyeStatusDto) => void; refresh: () => Promise<void> }): ReactElement {
  const { api } = useServices();
  const me = status.me;
  const inputId = useId();
  const [lines, setLines] = useState<SandalyeLine[]>(me?.lines ?? []);
  const [pending, setPending] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [note, setNote] = useState<string | null>(null);
  const [turnsLeft, setTurnsLeft] = useState(me?.turns_left ?? 0);
  const [ended, setEnded] = useState<SandalyeEndReason | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const endsAt = me?.ends_at ? new Date(me.ends_at).getTime() : now;
  const logRef = useRef<HTMLOListElement>(null);

  useEffect(() => {
    const t = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      window.clearInterval(t);
    };
  }, []);
  useEffect(() => {
    if (endsAt - now <= 0 && !ended) void refresh();
  }, [endsAt, now, ended, refresh]);
  useEffect(() => {
    logRef.current?.lastElementChild?.scrollIntoView({ block: "nearest" });
  }, [lines, pending]);

  const max = status.limits.max_chars;
  const busy = pending !== null;
  const send = (e: SyntheticEvent) => {
    e.preventDefault();
    const msg = text.trim();
    if (!msg || busy || ended) return;
    setNote(null);
    setLines((l) => [...l, { role: "ziyaretci", text: msg }]);
    setText("");
    setPending("");
    let answer = "";
    api
      .sandalyeMessage(msg, (ev) => {
        if (ev.t === "delta") {
          answer += ev.v;
          setPending(answer);
        } else if (ev.t === "done") setTurnsLeft(ev.turns_left);
        else if (ev.t === "end") setEnded(ev.reason);
        else {
          setNote(ev.message);
          // a filtered message is not part of the conversation
          setLines((l) => (l.at(-1)?.role === "ziyaretci" && l.at(-1)?.text === msg ? l.slice(0, -1) : l));
        }
      })
      .then(
        () => {
          if (answer) setLines((l) => [...l, { role: "balikci", text: answer }]);
          setPending(null);
        },
        (err: unknown) => {
          setPending(null);
          setNote(err instanceof ApiError ? err.message : "Mesaj gitmedi.");
        },
      );
  };

  return (
    <div className="z-chair__body">
      <p className="z-chair__meta" aria-live="off">
        <span>{up("kalan süre")} {mmss(endsAt - now)}</span>
        <span>
          {up("kalan mesaj")} {turnsLeft}
        </span>
      </p>
      <ol className="z-chat" ref={logRef} role="log" aria-live="polite" aria-label="Balıkçıyla konuşma">
        {lines.length === 0 && pending === null ? <li className="z-chat__hint">Derdini anlat. Tek dert, kısa ve öz.</li> : null}
        {lines.map((l, i) => (
          <li key={i} className={`z-chat__line is-${l.role}`}>
            <span className="z-chat__who">{up(l.role === "ziyaretci" ? "sen" : "balıkçı")}</span>
            <span className="z-chat__text">{l.text}</span>
          </li>
        ))}
        {pending !== null ? (
          <li className="z-chat__line is-balikci" aria-busy="true">
            <span className="z-chat__who">{up("balıkçı")}</span>
            <span className="z-chat__text">{pending || "…"}</span>
          </li>
        ) : null}
      </ol>
      {note ? (
        <p role="alert" className="z-chair__note">
          {note}
        </p>
      ) : null}
      {ended ? (
        <p className="z-chair__note" role="status">
          {END_TEXT[ended]}
        </p>
      ) : (
        <form className="z-chat__form" onSubmit={send}>
          <label htmlFor={inputId} className="z-label">
            {up("derdin")}
          </label>
          <textarea
            id={inputId}
            className="z-textarea"
            rows={3}
            maxLength={max}
            value={text}
            disabled={busy}
            onChange={(e) => {
              setText(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) send(e);
            }}
          />
          <span className={`z-small${text.length > max ? " is-over" : ""}`}>
            {text.length}/{max}
          </span>
          <button type="submit" className="z-btn" disabled={busy || !text.trim()}>
            {up(busy ? "balıkçı yazıyor" : "anlat")}
          </button>
        </form>
      )}
      <button
        type="button"
        className="z-btn z-btn--ghost"
        onClick={() => {
          void api.sandalyeLeave().then(onStatus, () => undefined);
        }}
      >
        {up("kalk")}
      </button>
    </div>
  );
}
